import OrderOptimizerDetailsCargoRequest from "../../api/raily/order-optimizer/details/cargo/order-optimizer-details-cargo.request";
import OrderOptimizerDetailsCargoResponse from "../../api/raily/order-optimizer/details/cargo/order-optimizer-details-cargo.response";
import OrderOptimizerDetailsOtherRequest from "../../api/raily/order-optimizer/details/other/order-optimizer-details-other.request";
import OrderOptimizerDetailsOtherResponse from "../../api/raily/order-optimizer/details/other/order-optimizer-details-other.response";
import OrderOptimizerListRequest from "../../api/raily/order-optimizer/list/order-optimizer-list.request";
import orderOptimizerApiService from "../../api/raily/order-optimizer/order-optimizer-api.service";
import OrderOptimizerPotentialDriversListRequest from "../../api/raily/order-optimizer/potential-drivers-list/order-optimizer-potential-drivers-list.request";
import OrderOptimizerPotentialDriversListResponse from "../../api/raily/order-optimizer/potential-drivers-list/order-optimizer-potential-drivers-list.response";
import dataLoadErrorFactory from "../../utils/data-load-error/data-load-error.factory";
import handleErrorFactory from "../../utils/handle-error/handle-error.factory";
import HttpError from "../../utils/http/http.error";
import SearchRoadRoutingRequest from "../../utils/search-road-route/search-road-routing.request";
import SearchRoadRoutingResponse from "../../utils/search-road-route/search-road-routing.response";
import OrderOptimizerDetailsCargo from "./details/cargo/order-optimizer-details-cargo";
import OrderOptimizerDetailsCargoParams from "./details/cargo/order-optimizer-details-cargo-params";
import orderOptimizerDetailsCargoParamsFactory from "./details/cargo/order-optimizer-details-cargo-params.factory";
import orderOptimizerDetailsCargoFactory from "./details/cargo/order-optimizer-details-cargo.factory";
import OrderOptimizerDetailsOther from "./details/other/order-optimizer-details-other";
import OrderOptimizerDetailsOtherParams from "./details/other/order-optimizer-details-other-params";
import orderOptimizerDetailsOtherParamsFactory from "./details/other/order-optimizer-details-other-params.factory";
import orderOptimizerDetailsOtherFactory from "./details/other/order-optimizer-details-other.factory";
import OrderOptimizerDetailsUpdateError from "./details/update/order-optimizer-details-update-error";
import OrderOptimizerDetailsUpdateParams from "./details/update/order-optimizer-details-update-params";
import orderOptimizerDetailsUpdateParamsFactory from "./details/update/order-optimizer-details-update-params.factory";
import OrderOptimizerList from "./list/order-optimizer-list";
import OrderOptimizerListError from "./list/order-optimizer-list-error";
import OrderOptimizerListParams from "./list/order-optimizer-list-params";
import orderOptimizerListParamsFactory from "./list/order-optimizer-list-params.factory";
import orderOptimizerListFactory from "./list/order-optimizer-list.factory";
import OrderOptimizerOptimizationStatus from "./optimization-status/order-optimizer-optimization-status";
import OrderOptimizerOptimizationStatusError from "./optimization-status/order-optimizer-optimization-status-error";
import OrderOptimizerOptimizationStatusParams from "./optimization-status/order-optimizer-optimization-status-params";
import orderOptimizerOptimizationStatusParamsFactory from "./optimization-status/order-optimizer-optimization-status-params.factory";
import orderOptimizerOptimizationStatusFactory from "./optimization-status/order-optimizer-optimization-status.factory";
import OrderOptimizerPotentialDriversList from "./potential-drivers-list/order-optimizer-potential-drivers-list";
import OrderOptimizerPotentialDriversListParams from "./potential-drivers-list/order-optimizer-potential-drivers-list-params";
import orderOptimizerPotentialDriversListParamsFactory from "./potential-drivers-list/order-optimizer-potential-drivers-list-params.factory";
import orderOptimizerPotentialDriversListFactory from "./potential-drivers-list/order-optimizer-potential-drivers-list.factory";
import OrderOptimizerRoadRouteItem from "./road-route/order-optimizer-road-route-item";
import OrderOptimizerRoadRouteParams from "./road-route/order-optimizer-road-route-params";
import orderOptimizerRoadRouteParamsFactory from "./road-route/order-optimizer-road-route-params.factory";
import orderOptimizerRoadRouteFactory from "./road-route/order-optimizer-road-route.factory";

const getList = async (
  params: OrderOptimizerListParams,
  abortSignal: AbortSignal,
): Promise<OrderOptimizerList> => {
  const request: OrderOptimizerListRequest =
    orderOptimizerListParamsFactory.create(params);

  try {
    const response = await orderOptimizerApiService().getList(
      request,
      abortSignal,
    );

    if (response.status === 404) {
      return orderOptimizerListFactory.createForEmptyData();
    }

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    const data = orderOptimizerListFactory.create(response);
    return data;
  } catch (_error) {
    const error = _error as HttpError | OrderOptimizerListError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const getRoute = async (
  params: OrderOptimizerRoadRouteParams,
): Promise<OrderOptimizerRoadRouteItem> => {
  const request: SearchRoadRoutingRequest =
    orderOptimizerRoadRouteParamsFactory.create(params);

  try {
    const response: SearchRoadRoutingResponse =
      await orderOptimizerApiService().getRoute(request);

    const RoadRouteItems: OrderOptimizerRoadRouteItem =
      orderOptimizerRoadRouteFactory.create(response);

    return RoadRouteItems;
  } catch (_error) {
    const error = _error as HttpError;

    throw handleErrorFactory.handleError(error);
  }
};

const getOtherDetails = async (
  params: OrderOptimizerDetailsOtherParams,
  abortSignal: AbortSignal,
): Promise<OrderOptimizerDetailsOther> => {
  const request: OrderOptimizerDetailsOtherRequest =
    orderOptimizerDetailsOtherParamsFactory.create(params);

  try {
    const response: OrderOptimizerDetailsOtherResponse =
      await orderOptimizerApiService().getOtherDetails(request, abortSignal);

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    const orderOptimizerDetailsOther: OrderOptimizerDetailsOther =
      orderOptimizerDetailsOtherFactory.create(response);

    return orderOptimizerDetailsOther;
  } catch (_error) {
    const error = _error as HttpError;

    throw handleErrorFactory.handleError(error);
  }
};

const getCargoDetails = async (
  params: OrderOptimizerDetailsCargoParams,
  abortSignal: AbortSignal,
): Promise<OrderOptimizerDetailsCargo> => {
  const request: OrderOptimizerDetailsCargoRequest =
    orderOptimizerDetailsCargoParamsFactory.create(params);

  try {
    const response: OrderOptimizerDetailsCargoResponse =
      await orderOptimizerApiService().getCargoDetails(request, abortSignal);

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    const orderOptimizerDetailsCargo: OrderOptimizerDetailsCargo =
      orderOptimizerDetailsCargoFactory.create(response);

    return orderOptimizerDetailsCargo;
  } catch (_error) {
    const error = _error as HttpError;

    throw handleErrorFactory.handleError(error);
  }
};

const getOptimizationStatus = async (
  params: OrderOptimizerOptimizationStatusParams,
  abortSignal: AbortSignal,
): Promise<OrderOptimizerOptimizationStatus> => {
  const request = orderOptimizerOptimizationStatusParamsFactory.create(params);

  try {
    const response = await orderOptimizerApiService().getOptimizationStatus(
      request,
      abortSignal,
    );

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    return orderOptimizerOptimizationStatusFactory.create(response);
  } catch (_error) {
    const error = _error as HttpError | OrderOptimizerOptimizationStatusError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const getPotentialDriversList = async (
  params: OrderOptimizerPotentialDriversListParams,
  abortSignal: AbortSignal,
): Promise<OrderOptimizerPotentialDriversList> => {
  const request: OrderOptimizerPotentialDriversListRequest =
    orderOptimizerPotentialDriversListParamsFactory.create(params);

  try {
    const response: OrderOptimizerPotentialDriversListResponse =
      await orderOptimizerApiService().getPotentialDriversList(
        request,
        abortSignal,
      );

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    const orderOptimizerPotentialDriversList: OrderOptimizerPotentialDriversList =
      orderOptimizerPotentialDriversListFactory.create(response.data);

    return orderOptimizerPotentialDriversList;
  } catch (_error) {
    const error = _error as HttpError;

    throw handleErrorFactory.handleError(error);
  }
};

const update = async (
  params: OrderOptimizerDetailsUpdateParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request = orderOptimizerDetailsUpdateParamsFactory.create(params);

  try {
    const response = await orderOptimizerApiService().update(
      request,
      abortSignal,
    );

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | OrderOptimizerDetailsUpdateError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const orderOptimizerService = {
  getList,
  getRoute,
  getOtherDetails,
  getCargoDetails,
  getOptimizationStatus,
  getPotentialDriversList,
  update,
};

export default orderOptimizerService;
